import {
  WheelPicker,
  WheelPickerWrapper,
  type WheelPickerOption,
} from '@ncdai/react-wheel-picker';
import '@ncdai/react-wheel-picker/style.css';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, ResponsiveModalBottomSheet } from '@/components';

import 'dayjs/locale/th';

type MonthValue = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

dayjs.extend(localeData);

interface BirthdaySelectionModalBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (date: Date) => void;
  initialDate?: Date;
}

const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

// This component collects only day + month (no year). We use a fixed leap year
// so February 29 remains selectable.
const BIRTHDAY_REFERENCE_YEAR = 2000;

const getDaysInMonth = (month: MonthValue): number => {
  return dayjs()
    .year(BIRTHDAY_REFERENCE_YEAR)
    .month(month - 1)
    .daysInMonth();
};

const BirthdaySelectionModalBottomSheet: React.FC<
  BirthdaySelectionModalBottomSheetProps
> = ({ isOpen, onClose, onConfirm, initialDate }) => {
  const { t, i18n } = useTranslation();

  const fallbackInitial = useMemo(() => dayjs('1995-01-01'), []);
  const initial = useMemo(
    () => (initialDate ? dayjs(initialDate) : fallbackInitial),
    [fallbackInitial, initialDate],
  );
  const initialMonth = clamp(initial.month() + 1, 1, 12) as MonthValue;
  const initialDay = clamp(initial.date(), 1, getDaysInMonth(initialMonth));

  const [month, setMonth] = useState<MonthValue>(initialMonth);
  const [day, setDay] = useState<number>(initialDay);

  const wasOpenRef = useRef(false);

  // Reset internal state when the sheet opens (useful for the test page).
  useEffect(() => {
    const isOpening = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (!isOpening) return;

    const openDate = initialDate ? dayjs(initialDate) : initial;
    const nextMonth = clamp(openDate.month() + 1, 1, 12) as MonthValue;
    const maxDay = getDaysInMonth(nextMonth);
    const nextDay = clamp(openDate.date(), 1, maxDay);

    setMonth(nextMonth);
    setDay(nextDay);
  }, [initial, initialDate, isOpen]);

  const months = useMemo(() => {
    const dayjsLocale = i18n.language.startsWith('th') ? 'th' : 'en';
    const monthNames = dayjs().locale(dayjsLocale).localeData().months();

    const options: WheelPickerOption<number>[] = monthNames.map(
      (label, index) => ({
        value: index + 1,
        label,
      }),
    );
    return options;
  }, [i18n.language]);

  const days = useMemo(() => {
    const maxDay = getDaysInMonth(month);
    const options: WheelPickerOption<number>[] = [];
    for (let d = 1; d <= maxDay; d += 1) {
      options.push({ value: d, label: d.toString().padStart(2, '0') });
    }
    return options;
  }, [month]);

  // Keep day in range when month/year changes.
  useEffect(() => {
    const maxDay = getDaysInMonth(month);
    if (day > maxDay) {
      setDay(maxDay);
    }
  }, [day, month]);

  const optionItemBase =
    'flex items-center justify-center text-black/35 text-[18px] leading-[32px]';
  const highlightItem =
    'flex items-center justify-center text-black text-[22px] leading-[32px]';

  return (
    <ResponsiveModalBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      closable
      showCloseOnMobile={true}
      ariaLabel={t('components.birthdaySelection.title')}
    >
      <div className="flex flex-col items-end gap-2 px-4 pt-4 pb-6">
        <p className="text-primary-text w-full text-center text-xl leading-7 font-semibold">
          {t('components.birthdaySelection.title')}
        </p>

        <div className="flex w-full flex-col items-center gap-4">
          <img
            src="/illustrations/birthday.png"
            alt="Birthday"
            className="h-40 w-auto"
          />

          <div className="relative h-[210px] max-w-[324px]">
            <div className="absolute inset-x-0 top-[85px] h-[32px] w-full rounded-lg bg-black/5" />

            <WheelPickerWrapper className="relative grid h-full grid-cols-2">
              <div className="flex justify-end pr-2">
                <div className="w-[84px]">
                  <WheelPicker
                    options={days}
                    value={day}
                    onValueChange={(v) => setDay(Number(v))}
                    infinite
                    optionItemHeight={32}
                    classNames={{
                      optionItem: `${optionItemBase} justify-end text-right`,
                      highlightWrapper: 'bg-bg-selected',
                      highlightItem: `${highlightItem} justify-end text-right`,
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-start pl-2">
                <div className="w-[190px]">
                  <WheelPicker
                    options={months}
                    value={month}
                    onValueChange={(v) => setMonth(Number(v) as MonthValue)}
                    infinite
                    optionItemHeight={32}
                    classNames={{
                      optionItem: optionItemBase,
                      highlightWrapper: 'bg-bg-selected',
                      highlightItem,
                    }}
                  />
                </div>
              </div>
            </WheelPickerWrapper>
          </div>

          <div className="w-full">
            <Button
              onClick={() => {
                const selected = dayjs()
                  .year(BIRTHDAY_REFERENCE_YEAR)
                  .month(month - 1)
                  .date(day)
                  .startOf('day');

                onConfirm(selected.toDate());
                onClose();
              }}
              type="button"
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </div>
    </ResponsiveModalBottomSheet>
  );
};

export default BirthdaySelectionModalBottomSheet;
